import { useState } from 'react';
import { Plus, Edit, Trash2, Package } from 'lucide-react';
import type { PremiumModule } from '@yawi/types';
import { Button, Table, Badge, Card, CardHeader } from '@/core/ui/components';
import { useConfirmation } from '@/app/providers';
import { usePremiumModules } from '../hooks/useBilling';
import { ModuleFormModal } from './ModuleFormModal';

export function ModulesTab() {
  const { confirm } = useConfirmation();
  const { modules, isLoading, deleteModule, refetch } = usePremiumModules();

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingModule, setEditingModule] = useState<PremiumModule | undefined>();

  const handleEdit = (module: PremiumModule) => {
    setEditingModule(module);
    setShowFormModal(true);
  };

  const handleDelete = async (module: PremiumModule) => {
    const confirmed = await confirm({
      title: 'Delete Module',
      message: `Are you sure you want to delete "${module.name}"? Tenants using this module will lose access.`,
      variant: 'danger',
      confirmText: 'Delete',
    });

    if (confirmed) {
      await deleteModule(module.id);
    }
  };

  const handleCloseModal = () => {
    setShowFormModal(false);
    setEditingModule(undefined);
  };

  const columns = [
    {
      key: 'name',
      header: 'Module',
      render: (module: PremiumModule) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
            <Package className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <p className="font-medium text-theme">{module.name}</p>
            <p className="text-sm text-theme-tertiary">{module.code}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (module: PremiumModule) => (
        <span className="text-theme-secondary text-sm">{module.description}</span>
      ),
    },
    {
      key: 'price',
      header: 'Price',
      render: (module: PremiumModule) => (
        <div>
          <span className="font-medium text-theme">
            ${module.monthlyPrice}/mo
          </span>
          {module.yearlyPrice != null && module.yearlyPrice > 0 && (
            <span className="block text-xs text-theme-tertiary">
              ${module.yearlyPrice}/yr
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (module: PremiumModule) => (
        <Badge variant={module.isActive ? 'success' : 'default'}>
          {module.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-24',
      render: (module: PremiumModule) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => handleEdit(module)}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <Edit className="w-4 h-4 text-theme-secondary" />
          </button>
          <button
            onClick={() => handleDelete(module)}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <Trash2 className="w-4 h-4 text-red-500" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader
        title="Premium Modules"
        description="Add-on features that can be purchased separately"
        action={
          <Button
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => setShowFormModal(true)}
          >
            Add Module
          </Button>
        }
      />

      <Table
        columns={columns}
        data={modules}
        keyExtractor={(module) => module.id}
        isLoading={isLoading}
        emptyMessage="No premium modules configured"
      />

      {showFormModal && (
        <ModuleFormModal
          module={editingModule}
          onClose={handleCloseModal}
          onSuccess={() => {
            handleCloseModal();
            refetch();
          }}
        />
      )}
    </Card>
  );
}
